import React, { useState, useRef, useEffect } from 'react';
import ScrollArea from './scrollArea';

interface Option {
  value: string;
  label: string;
}

interface SimpleDropdownProps {
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: Option[];
  label?: string;
  placeholder?: string;
  className?: string;
  isLoading?: boolean;
  allowOther?: boolean;
  otherValue?: string;
  onOtherChange?: (value: string) => void;
}

const SimpleDropdown: React.FC<SimpleDropdownProps> = ({
  name,
  value,
  onChange,
  options,
  label,
  placeholder = 'Select an option',
  className = '',
  isLoading = false,
  allowOther = false,
  otherValue = '',
  onOtherChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showOtherInput, setShowOtherInput] = useState(value === 'other');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const otherInputRef = useRef<HTMLInputElement>(null);

  const handleOptionClick = (optionValue: string) => {
    const syntheticEvent = {
      target: {
        name,
        value: optionValue
      }
    } as React.ChangeEvent<HTMLSelectElement>;
    
    onChange(syntheticEvent);
    
    if (optionValue === 'other' && allowOther) {
      setShowOtherInput(true);
      setTimeout(() => otherInputRef.current?.focus(), 0);
    } else {
      setShowOtherInput(false);
      setIsOpen(false);
    }
  };

  const handleOtherInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (onOtherChange) {
      onOtherChange(newValue);
    }
  };

  const handleOtherInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && otherValue.trim()) {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = value === 'other' ? otherValue : selectedOption?.label || '';

  return (
    <div className="relative" ref={dropdownRef}>
      {label && <label className="block mb-2 text-sm font-normal text-[#75777C]">{label}</label>}
      <div
        onClick={() => !isLoading && setIsOpen(!isOpen)}
        className={`relative cursor-pointer flex items-center justify-between border border-[#EEEFF1] rounded-[10px] ${className}`}
      >
        <input
          type="text"
          readOnly
          value={displayValue}
          placeholder={placeholder}
          className="w-[90%] cursor-pointer truncate"
        />
        <svg
          className={`w-4 h-4 absolute right-3 transition-transform ${isOpen ? 'transform rotate-180' : ''} text-[#222222]`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && !isLoading && (
        <div className="absolute z-10 w-full mt-1 bg-[#FFFFFF] rounded-[10px] shadow-lg border border-[#EEEFF1]">
          <ScrollArea maxHeight="180px">
            <div className="py-1">
              {options.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleOptionClick(option.value)}
                  className={`px-4 py-2 text-xs cursor-pointer ${
                    value === option.value 
                      ? 'bg-[#F5F5F5] text-[#222222]' 
                      : 'hover:bg-[#F5F5F5] text-[#222222]'
                  }`}
                >
                  {option.label}
                </div>
              ))}
              {allowOther && (
                <div className="px-4 py-2">
                  {showOtherInput ? (
                    <input
                      ref={otherInputRef}
                      type="text"
                      value={otherValue}
                      onChange={handleOtherInputChange}
                      onKeyDown={handleOtherInputKeyDown}
                      placeholder="Enter custom value"
                      className="w-full px-2 py-1 text-sm border border-[#EEEFF1] rounded-lg
                        focus:outline-none focus:ring-1 focus:ring-[#29DC5B] focus:border-transparent"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <div
                      onClick={() => handleOptionClick('other')}
                      className={`cursor-pointer text-xs ${
                        value === 'other'
                          ? 'bg-[#F5F5F5] text-[#222222]'
                          : 'hover:bg-[#F5F5F5] text-[#222222]'
                      }`}
                    >
                      Other
                    </div>
                  )}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
};

export default SimpleDropdown;
